/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Fade,
  // eslint-disable-next-line no-restricted-imports
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  Slide,
  useMediaQuery,
} from '@material-ui/core';
import { Theme, useTheme } from '@emotion/react';
import { TransitionProps } from '@material-ui/core/transitions';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { base } from 'omakase/Dialog/dialogCoreCssDefs';
import { forwardRef } from 'react';

export type DialogEntranceMotion = 'down' | 'up' | 'fadeIn' | never;
export type DialogVariant = 'fullScreen' | 'partial' | 'centered' | 'sheet' | never;

/**
 * DialogType props should be composed using types from DialogEntranceMotion and DialogType
 */
export interface DialogCoreTypeProps {
  /**
   * For mobile-sized screens - whether to cover the whole screen from the entrance direction, part of the screen,
   * performing a menu-type action, or showing a mid-screen window.
   */
  variant: DialogVariant;
  /**
   * For mobile-sized screens - how the dialog transitions from absent to present.
   */
  entranceMotion: DialogEntranceMotion;
  /**
   * For desktop-sized screens - whether to cover the whole screen from the entrance direction, part of the screen,
   * performing a menu-type action, or showing a mid-screen window.
   */
  variantDesktop: DialogVariant;
  /**
   * For desktop-sized screens - how the dialog transitions from absent to present.
   */
  entranceMotionDesktop: DialogEntranceMotion;
}

const TransitionUp = forwardRef<unknown, TransitionProps>(function Transition(
  { children, ...rest },
  ref
) {
  return (
    <Slide direction="up" ref={ref} timeout={200} {...rest}>
      {children as any}
    </Slide>
  );
});

const TransitionDown = forwardRef<unknown, TransitionProps>(function Transition(
  { children, ...rest },
  ref
) {
  return (
    <Slide direction="down" ref={ref} timeout={200} {...rest}>
      {children as any}
    </Slide>
  );
});

const TransitionFadeIn = forwardRef<unknown, TransitionProps>(function Transition(
  { children, ...rest },
  ref
) {
  return (
    <Fade ref={ref} timeout={200} {...rest}>
      {children as any}
    </Fade>
  );
});

const getTransitionComponent = (entranceMotion: DialogEntranceMotion) => {
  switch (entranceMotion) {
    case 'up':
      return TransitionUp;
    case 'down':
      return TransitionDown;
    case 'fadeIn':
      return TransitionFadeIn;
    default:
      assertNever(entranceMotion);
  }
};

interface GetDialogVariantOptions {
  fullScreen?: boolean;
  isDesktop: boolean;
}

const getDialogVariant = (
  variant: DialogVariant,
  variantDesktop: DialogVariant,
  { fullScreen, isDesktop }: GetDialogVariantOptions
): DialogVariant => {
  if (fullScreen) {
    return 'fullScreen';
  }
  return isDesktop ? variantDesktop : variant;
};

interface GetDialogEntranceMotionOptions {
  isDesktop: boolean;
}

const getDialogEntranceMotion = (
  entranceMotion: DialogEntranceMotion,
  entranceMotionDesktop: DialogEntranceMotion,
  { isDesktop }: GetDialogEntranceMotionOptions
): DialogEntranceMotion => (isDesktop ? entranceMotionDesktop : entranceMotion);

export interface DialogCoreBaseProps extends MuiDialogProps {
  fullHeight?: boolean;
  maxContent?: boolean;
  ['data-testid']?: string;
}

export type DialogCoreProps = DialogCoreTypeProps & DialogCoreBaseProps;

export const DialogCore = ({
  entranceMotion,
  entranceMotionDesktop,
  variant,
  variantDesktop,
  fullScreen,
  open,
  onClose,
  fullHeight = false,
  fullWidth,
  maxWidth,
  maxContent = false,
  'aria-label': ariaLabel,
  TransitionProps,
  PaperProps,
  ...rest
}: DialogCoreProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  const { duration } = theme.timings;
  const shorter = parseInt(duration.shorter, 10);
  const slow = parseInt(duration.slow, 10);

  const effectiveEntranceMotion = getDialogEntranceMotion(entranceMotion, entranceMotionDesktop, {
    isDesktop,
  });
  const effectiveVariant = getDialogVariant(variant, variantDesktop, { fullScreen, isDesktop });

  const TransitionComponent = getTransitionComponent(effectiveEntranceMotion);

  return (
    <MuiDialog
      {...rest}
      open={open}
      fullScreen={
        effectiveVariant === 'fullScreen' ||
        (!isDesktop && (effectiveVariant === 'sheet' || effectiveVariant === 'partial'))
      }
      css={(theme: Theme) =>
        base(theme, effectiveVariant, effectiveEntranceMotion, {
          isDesktop,
          fullHeight,
          maxContent,
        })
      }
      onClose={onClose}
      maxWidth={maxWidth}
      BackdropProps={{
        transitionDuration: { enter: slow, exit: shorter },
      }}
      TransitionComponent={TransitionComponent}
      TransitionProps={{ ...TransitionProps, role: 'presentation' } as any} // TK-6147: https://github.com/mui-org/material-ui/issues/18935#issuecomment-569287547
      PaperProps={{ ...PaperProps, 'aria-label': ariaLabel }}
      fullWidth={fullWidth}
    />
  );
};
